// ─────────────────────────────────────────────────────────────────────────
// Notas y anulación — notas de crédito (61) sobre facturas emitidas desde el ERP.
// Solo admin y accountant (el backend de /dte/emitir-nc igual lo exige).
//
// Tres acciones sobre la factura elegida, todas por POST /dte/emitir-nc con
// origen: "modulo":
//   · Anular la factura        → codigoRef 1 (espeja el detalle y revierte saldos)
//   · NC por texto             → codigoRef 2 (sin montos)
//   · NC por montos            → codigoRef 3 (con ítems nombre/qty/precio netos)
// Una NC es un documento real ante el SII: no se borra, se corrige con otra.
// La nota de débito (56) no se construye: requiere CAF 56.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import { S, fmt, fmtD, periodoHoy, labelPeriodo, ultimosPeriodos } from "./ui";

const ACCIONES = [
  { id: 1, label: "Anular la factura", color: "#e74c3c" },
  { id: 2, label: "Nota de crédito por texto", color: "#f39c12" },
  { id: 3, label: "Nota de crédito por montos", color: "#a78bfa" },
];

const NotasYAnulacion = ({ roles = [], esProduccion = null }) => {
  const isMobile = useIsMobile();
  const puede = roles.some(r => ["admin", "accountant"].includes(r));
  const [periodo, setPeriodo] = useState(periodoHoy());   // "" = todas
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [errorLista, setErrorLista] = useState("");
  const [factura, setFactura] = useState(null);
  const [accion, setAccion] = useState(1);
  const [motivo, setMotivo] = useState("");
  const [entiendo, setEntiendo] = useState(false);
  const [items, setItems] = useState([{ nombre: "", qty: 1, precio: "" }]);
  const [cargandoItems, setCargandoItems] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);
  const periodos = useMemo(() => ultimosPeriodos(12), []);

  const pedidoRef = useRef(0);
  const itemsDeRef = useRef(null);
  const cargar = useCallback(async () => {
    const n = ++pedidoRef.current;
    setCargando(true); setErrorLista("");
    try {
      // El listado filtra el tipo con `tipo` (33 = facturas).
      const params = { tipo: 33 };
      if (periodo) params.periodo = periodo;
      if (q.trim()) params.q = q.trim();
      const { data } = await backend.get("dte/listado", { params });
      if (n === pedidoRef.current) setDocs(data.documentos || []);
    } catch (e) { if (n === pedidoRef.current) setErrorLista(e?.response?.data?.error || e.message); }
    if (n === pedidoRef.current) setCargando(false);
  }, [periodo, q]);
  useEffect(() => { const t = setTimeout(cargar, q ? 350 : 0); return () => clearTimeout(t); }, [cargar, q]);

  // Código 3: precarga los ítems reales de la factura (editables). Si no vienen, queda la línea de partida.
  useEffect(() => {
    if (accion !== 3 || !factura?.id || itemsDeRef.current === factura.id) return undefined;
    const idPedido = factura.id;
    itemsDeRef.current = idPedido;   // una sola vez por factura: volver al código 3 no pisa lo editado
    setCargandoItems(true);
    backend.get(`dte/emitido/${idPedido}/items`)
      .then(({ data }) => {
        if (itemsDeRef.current !== idPedido) return;   // se eligió otra factura mientras tanto
        const reales = (data.items || [])
          .filter(it => it.nombre && Number(it.qty) > 0 && Number(it.precio) > 0)
          .map(it => ({ nombre: it.nombre, qty: it.qty, precio: it.precio, sku: it.sku || null }));
        if (reales.length) setItems(reales);
      })
      .catch(() => { /* sin ítems reales queda la línea editable de partida */ })
      .finally(() => { if (itemsDeRef.current === idPedido) setCargandoItems(false); });
    return undefined;
  }, [accion, factura?.id]);

  if (!puede) return <div style={{ ...S.card, color: "#8a8a8a", fontSize: 12 }}>Las notas de crédito las emite admin o contabilidad.</div>;

  const elegir = d => {
    if (d.id === factura?.id) return;   // misma factura: no pisar lo editado
    itemsDeRef.current = null;   // la nueva factura vuelve a pedir sus ítems reales
    setCargandoItems(false);     // y se descarta la carga de la anterior
    setFactura(d); setResultado(null); setError(""); setEntiendo(false); setMotivo("");
    // Punto de partida de la NC por montos: una línea por el neto de la factura, editable;
    // al elegir el código 3 se reemplaza por los ítems reales si la bitácora los guardó.
    setItems([{ nombre: `CORRECCION F${d.folio}`, qty: 1, precio: d.montoNeto || "" }]);
  };
  const cambiarAccion = id => { setAccion(id); setError(""); setEntiendo(false); };
  const cambiarItem = (i, campo, valor) => setItems(prev => prev.map((x, j) => (j === i ? { ...x, [campo]: valor } : x)));

  const anulada = !!factura?.anuladaPor;
  const itemsValidos = items.filter(it => String(it.nombre).trim() && Number(it.qty) > 0 && Number(it.precio) > 0);
  const netoNc = itemsValidos.reduce((s, it) => s + Math.round(Number(it.qty) * Number(it.precio)), 0);

  const emitir = async () => {
    if (!factura || enviando) return;
    setError("");
    if (!motivo.trim()) { setError("El motivo es obligatorio (queda en la referencia del DTE)."); return; }
    if (accion === 1 && !entiendo) { setError("Debe marcar la confirmación para anular la factura."); return; }
    if (accion === 3 && !itemsValidos.length) { setError("Ingrese al menos un ítem con nombre, cantidad y precio neto."); return; }
    const texto = accion === 1 ? "que ANULA" : "que corrige";
    const ambienteTxt = esProduccion == null ? "ambiente no confirmado" : esProduccion ? "PRODUCCIÓN (documento tributario real)" : "certificación (ambiente de prueba)";
    if (!window.confirm(`Se emitirá una nota de crédito ${texto} la factura N° ${factura.folio}.\nAmbiente: ${ambienteTxt}.\n\n¿Emitir?`)) return;
    setEnviando(true);
    try {
      const body = {
        // En producción la madre es la factura del libro (facturaId); en certificación, la fila de la bitácora.
        facturaId: factura.facturaId || factura.id,
        codigoRef: accion, motivo: motivo.trim(), origen: "modulo",
      };
      if (accion === 3) body.items = itemsValidos.map(it => ({ nombre: String(it.nombre).trim().slice(0, 80), sku: it.sku || null, qty: Math.round(Number(it.qty)), precio: Number(it.precio) }));
      const { data } = await backend.post("dte/emitir-nc", body);
      setResultado(data);
      await cargar();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      setError(e?.response?.status === 409 ? `Aviso: ${msg}` : msg);
    }
    setEnviando(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 12, color: "#9aa0a6", lineHeight: 1.5 }}>
        Busque la factura emitida desde el ERP y elija la acción. Una nota de crédito es un documento real ante el SII:
        no se borra, se corrige con otra.
      </div>

      {/* 1 · Buscar y elegir la factura */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={{ ...S.input, flex: isMobile ? "1 1 110px" : undefined }}>
          <option value="">Todas</option>
          {periodos.map(p => <option key={p} value={p}>{labelPeriodo(p)}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="folio, cliente, RUT, OC…" style={{ ...S.input, flex: "1 1 180px" }} />
      </div>
      {errorLista && <div style={{ color: "#e74c3c", fontSize: 12 }}>{errorLista}</div>}
      <div style={{ ...S.card, padding: 0, maxHeight: 260, overflowY: "auto" }}>
        {docs === null ? (
          <div style={{ padding: 12, color: "#8a8a8a", fontSize: 12 }}>{cargando ? "Cargando…" : ""}</div>
        ) : docs.length === 0 ? (
          <div style={{ padding: 12, color: "#8a8a8a", fontSize: 12 }}>No hay facturas emitidas desde el ERP con esos filtros.</div>
        ) : docs.map(d => {
          const sel = factura?.id === d.id;
          return (
            <div key={d.id} onClick={() => elegir(d)} style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid #252525", cursor: "pointer", background: sel ? "#1e2a38" : "transparent", opacity: d.anuladaPor ? 0.6 : 1 }}>
              <div style={{ minWidth: 0 }}>
                <span style={S.pill("#2ecc71")}>F {d.folio}</span>
                <span style={{ color: "#fff", marginLeft: 8, fontSize: 12, fontWeight: 700 }}>{d.razonReceptor || d.rutReceptor}</span>
                <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>{fmtD(d.fecha)}{d.ots?.length ? ` · OT ${d.ots.join(", ")}` : ""}{d.anuladaPor ? ` · anulada por NC ${d.anuladaPor.folio}` : ""}</div>
              </div>
              <span style={{ color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: 12 }}>{fmt(d.montoTotal)}</span>
            </div>
          );
        })}
      </div>

      {/* 2 · Acción sobre la factura elegida */}
      {factura && (
        <div style={{ ...S.card, borderColor: "#4a2020", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={S.label}>Factura {factura.folio} · {factura.razonReceptor || factura.rutReceptor} · {fmt(factura.montoTotal)}</div>
          {anulada && (
            <div style={{ color: "#f39c12", fontSize: 12 }}>Esta factura ya fue anulada por la NC {factura.anuladaPor.folio}: no corresponde emitir otra nota de crédito.</div>
          )}

          <div className="chips-scroll" style={{ display: "flex", gap: 6, flexWrap: isMobile ? "nowrap" : "wrap", overflowX: "auto" }}>
            {ACCIONES.map(a => (
              <button key={a.id} onClick={() => cambiarAccion(a.id)} disabled={anulada || enviando}
                style={{ ...S.btn(a.color, accion === a.id), padding: "7px 12px", opacity: anulada ? 0.4 : 1 }}>{a.label}</button>
            ))}
            <button disabled title="Requiere CAF 56; no disponible todavía" style={{ ...S.btn("#666"), padding: "7px 12px", cursor: "not-allowed", opacity: 0.6 }}>Nota de débito (56)</button>
          </div>
          <div style={{ fontSize: 10, color: "#666" }}>Nota de débito: requiere CAF 56; no disponible todavía.</div>

          <div>
            <div style={S.label}>Motivo (obligatorio)</div>
            <input value={motivo} onChange={e => setMotivo(e.target.value)} disabled={enviando || anulada}
              placeholder="ej: se facturó dos veces la OC 35612" style={{ ...S.input, width: "100%" }} />
          </div>

          {accion === 3 && !anulada && (
            <div>
              <div style={S.label}>Ítems de la nota (precios netos){cargandoItems ? " · cargando ítems de la factura…" : ""}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                {items.map((it, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <input value={it.nombre} onChange={e => cambiarItem(i, "nombre", e.target.value)} disabled={cargandoItems} placeholder="Descripción" style={{ ...S.input, flex: "3 1 160px" }} />
                    <input type="number" min={1} value={it.qty} onChange={e => cambiarItem(i, "qty", e.target.value)} disabled={cargandoItems} style={{ ...S.input, width: 64, textAlign: "right" }} />
                    <input type="number" min={0} value={it.precio} onChange={e => cambiarItem(i, "precio", e.target.value)} disabled={cargandoItems} placeholder="Precio neto" style={{ ...S.input, width: 100, textAlign: "right" }} />
                    <button style={S.btn("#8a8a8a")} onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} disabled={items.length === 1}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                <button style={S.btn("#3498db")} onClick={() => setItems(prev => [...prev, { nombre: "", qty: 1, precio: "" }])}>+ Ítem</button>
                <span style={{ fontSize: 11, color: "#8a8a8a" }}>Neto de la NC: {fmt(netoNc)} (factura: {fmt(factura.montoNeto)})</span>
              </div>
            </div>
          )}

          {accion === 1 && !anulada && (
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "#e6b0aa", cursor: "pointer" }}>
              <input type="checkbox" checked={entiendo} onChange={e => setEntiendo(e.target.checked)} style={{ marginTop: 2 }} />
              <span>Entiendo que esto emite una nota de crédito real al SII que anula la factura N° {factura.folio}</span>
            </label>
          )}

          {error && <div style={{ color: error.startsWith("Aviso") ? "#f39c12" : "#e74c3c", fontSize: 12 }}>{error}</div>}
          {resultado && (
            <div style={{ color: "#2ecc71", fontSize: 12 }}>
              {resultado.prueba ? "NC de prueba emitida" : "Nota de crédito emitida"}{resultado.folio ? ` · folio ${resultado.folio}` : ""}.
            </div>
          )}

          <div>
            <button onClick={emitir} disabled={enviando || anulada || !motivo.trim() || (accion === 1 && !entiendo)}
              style={{ ...S.btn(accion === 1 ? "#e74c3c" : "#3498db", true), padding: "8px 16px", opacity: (enviando || anulada || !motivo.trim() || (accion === 1 && !entiendo)) ? 0.5 : 1 }}>
              {enviando ? "Emitiendo…" : accion === 1 ? "Anular con nota de crédito" : "Emitir nota de crédito"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotasYAnulacion;
